// POS receiving account (phase 3 R-PAY) — JS twin of the bo-api helpers
// (controllers/POS-P3Helpers.go: posThaiBanks, posFormatBankAccount,
// posThaiTaxIDValid). Same vectors as the Go / pos-fn twins (spec §8.4).

// Dropdown order = spec §3.4.
export const THAI_BANKS = [
	{ code: "KBANK", name: "ธนาคารกสิกรไทย" },
	{ code: "SCB", name: "ธนาคารไทยพาณิชย์" },
	{ code: "BBL", name: "ธนาคารกรุงเทพ" },
	{ code: "KTB", name: "ธนาคารกรุงไทย" },
	{ code: "BAY", name: "ธนาคารกรุงศรีอยุธยา" },
	{ code: "TTB", name: "ธนาคารทหารไทยธนชาต" },
	{ code: "GSB", name: "ธนาคารออมสิน" },
	{ code: "BAAC", name: "ธ.ก.ส." },
	{ code: "GHB", name: "ธนาคารอาคารสงเคราะห์" },
	{ code: "UOB", name: "ธนาคารยูโอบี" },
	{ code: "CIMB", name: "ธนาคารซีไอเอ็มบี ไทย" },
	{ code: "KKP", name: "ธนาคารเกียรตินาคินภัทร" },
	{ code: "LHB", name: "ธนาคารแลนด์ แอนด์ เฮ้าส์" },
	{ code: "TISCO", name: "ธนาคารทิสโก้" }
];

export const PAY_MODES = ["promptpay", "static_qr_image", "bank_account_only"];
export const PROMPTPAY_TYPES = ["phone", "tax_id", "ewallet"];
export const QR_IMAGE_MAX_BYTES = 300 * 1024;

export function digitsOnly(s) {
	return String(s === null || s === undefined ? "" : s).replace(/\D/g, "");
}

export function bankName(code) {
	const b = THAI_BANKS.find((x) => x.code === code);
	return b ? b.name : "";
}

// "ธ." + name without "ธนาคาร" (BAAC stays "ธ.ก.ส."), as on receipts.
export function bankShortName(code) {
	const n = bankName(code);
	if (!n) return "";
	if (n.indexOf("ธนาคาร") === 0) return "ธ." + n.slice("ธนาคาร".length);
	return n;
}

// 10 digits → XXX-X-XXXXX-X; any other length → digits unchanged.
export function formatBankAccount(bankCode, digits) {
	const d = digitsOnly(digits);
	if (d.length !== 10) return d;
	return `${d.slice(0, 3)}-${d.slice(3, 4)}-${d.slice(4, 9)}-${d.slice(9)}`;
}

// Σ dᵢ×(13−i) for i = 0..11; check = (11 − Σ mod 11) mod 10 = d₁₂.
export function isThaiTaxIdValid(s) {
	if (typeof s !== "string" || !/^\d{13}$/.test(s)) return false;
	let sum = 0;
	for (let i = 0; i < 12; i++) sum += Number(s[i]) * (13 - i);
	return (11 - (sum % 11)) % 10 === Number(s[12]);
}

// Decoded byte size of a base64 data URL (approximate, like the server).
export function dataUrlBytes(dataUrl) {
	const s = String(dataUrl || "");
	const i = s.indexOf(",");
	const b64 = (i < 0 ? s : s.slice(i + 1)).replace(/=+$/, "");
	return Math.floor((b64.length * 3) / 4);
}

export function promptpayIdValid(type, id) {
	const d = digitsOnly(id);
	if (type === "phone") return /^0\d{9}$/.test(d);
	if (type === "tax_id") return isThaiTaxIdValid(d);
	if (type === "ewallet") return /^\d{15}$/.test(d);
	return false;
}

// Client-side twin of posValidatePaymentAccount → list of bad field names.
// hasStoredQr: a static_qr_image account already has an image on the server
// (a later PUT without qr_image_data_url keeps it).
export function validatePaymentAccount(a, hasStoredQr) {
	const bad = [];
	const acc = a || {};
	if (PAY_MODES.indexOf(acc.mode) === -1) bad.push("mode");
	if (!bankName(acc.bank_code)) bad.push("bank_code");
	const no = digitsOnly(acc.account_no);
	if (no.length < 10 || no.length > 15) bad.push("account_no");
	const name = String(acc.account_name || "").trim();
	if (name.length < 1 || name.length > 150) bad.push("account_name");
	if (acc.mode === "promptpay") {
		if (PROMPTPAY_TYPES.indexOf(acc.promptpay_type) === -1) bad.push("promptpay_type");
		else if (!promptpayIdValid(acc.promptpay_type, acc.promptpay_id)) bad.push("promptpay_id");
	}
	if (acc.mode === "static_qr_image") {
		const img = acc.qr_image_data_url || "";
		if (img) {
			if (!/^data:image\/(png|jpeg);base64,/.test(img) || dataUrlBytes(img) > QR_IMAGE_MAX_BYTES) bad.push("qr_image_data_url");
		} else if (!hasStoredQr) bad.push("qr_image_data_url");
	}
	return bad;
}

// Request body for B48/B49 from the form (digits only; unused fields null).
export function paymentAccountBody(a, withEnabled) {
	const acc = a || {};
	const body = {
		mode: acc.mode,
		bank_code: acc.bank_code,
		account_no: digitsOnly(acc.account_no),
		account_name: String(acc.account_name || "").trim(),
		promptpay_type: acc.mode === "promptpay" ? acc.promptpay_type || null : null,
		promptpay_id: acc.mode === "promptpay" ? digitsOnly(acc.promptpay_id) || null : null,
		show_on_receipt: acc.show_on_receipt !== false
	};
	if (acc.mode === "static_qr_image" && acc.qr_image_data_url) body.qr_image_data_url = acc.qr_image_data_url;
	if (withEnabled) body.enabled = acc.enabled !== false;
	return body;
}

// Form model from a B48/B49 object (null → the default KBank empty form).
export function paymentAccountForm(obj) {
	const o = obj || {};
	return {
		mode: PAY_MODES.indexOf(o.mode) === -1 ? "bank_account_only" : o.mode,
		bank_code: o.bank_code || "KBANK",
		account_no: formatBankAccount(o.bank_code, o.account_no || ""),
		account_name: o.account_name || "",
		promptpay_type: o.promptpay_type || "tax_id",
		promptpay_id: o.promptpay_id || "",
		qr_image_data_url: o.qr_image_data_url || "",
		show_on_receipt: o.show_on_receipt !== false,
		enabled: o.enabled !== false
	};
}

// "โอนเข้า ธ.กสิกรไทย 021-1-48198-6 บริษัท มาดามฟิน จำกัด" (receipt line twin).
export function paymentAccountText(a) {
	const acc = a || {};
	const parts = ["โอนเข้า"];
	const short = bankShortName(acc.bank_code);
	if (short) parts.push(short);
	const no = formatBankAccount(acc.bank_code, acc.account_no);
	if (no) parts.push(no);
	const name = String(acc.account_name || "").trim();
	if (name) parts.push(name);
	return parts.join(" ");
}

// ---------------------------------------------------------------------------
// PromptPay EMV payload (preview only; the POS app makes the real QR).
// Same output as the `promptpay-qr` package for phone / tax-ID / e-Wallet ids.
// ---------------------------------------------------------------------------
function tlv(tag, value) {
	return tag + String(value.length).padStart(2, "0") + value;
}

export function crc16Ccitt(s) {
	let crc = 0xffff;
	for (let i = 0; i < s.length; i++) {
		crc ^= s.charCodeAt(i) << 8;
		for (let b = 0; b < 8; b++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
	}
	return crc.toString(16).toUpperCase().padStart(4, "0");
}

// promptPayPayload(id, amountBaht?) — id: 10-digit phone, 13-digit tax ID or
// 15-digit e-Wallet (digits only). Returns "" for an id of any other length.
export function promptPayPayload(id, amountBaht) {
	const d = digitsOnly(id);
	let target;
	if (d.length === 10) target = tlv("01", ("0000000000000" + d.replace(/^0/, "66")).slice(-13));
	else if (d.length === 13) target = tlv("02", d);
	else if (d.length === 15) target = tlv("03", d);
	else return "";
	const hasAmount = amountBaht !== undefined && amountBaht !== null && Number(amountBaht) > 0;
	let p = tlv("00", "01") + tlv("01", hasAmount ? "12" : "11") + tlv("29", tlv("00", "A000000677010111") + target) + tlv("58", "TH") + tlv("53", "764");
	if (hasAmount) p += tlv("54", Number(amountBaht).toFixed(2));
	p += "6304";
	return p + crc16Ccitt(p);
}
